import { ILoadingProps, Loading } from "./Loading";
import React, { useState } from "react";

/**
 * Control to change the isLoading state to be used in part with
 * the `LoadingContainer` component.
 *
 * Need just the state of isLoading? see `ILoadingValue`
 *
 * Need both? see `ILoadingState`
 */
export interface ILoadingControl {
    /**
     * Updates the isLoading state within the `LoadingContainer` component.
     */
    setIsLoading: (isLoading: boolean) => void;
}

/**
 * Value of the isLoading state to be used in part with the
 * `LoadingContainer` component.
 *
 * Need just the control to change isLoading? see `ILoadingControl`
 *
 * Need both? see `ILoadingState`
 */
export interface ILoadingValue {
    /**
     * The current state of isLoading in the `LoadingContainer` component.
     */
    isLoading: boolean;
}

/**
 * Loading state object for getting the current value of `isLoading`
 * and updating this value within the `LoadingContainer` component.
 */
export type ILoadingState = ILoadingControl & ILoadingValue;

/**
 * Properties for the LoadingContainer component which includes
 * optional properties for the `Loading` component.
 */
export interface ILoadingContainerProps {
    /**
     * Properties for the `Loading` component.
     */
    loadingProps?: ILoadingProps;

    /**
     * A function that takes the loading state information and
     * creates an Element with it. This should be the child of the
     * component
     */
    children: (loading: ILoadingState) => JSX.Element;
}

/**
 * A Loading container which helps support components that require
 * loading without that component having to worry about managing state
 * or the rendering of the `Loading` component.
 *
 * This component provides the `ILoadingState` object to the child
 * component through the function - this allows the child component
 * to use the setIsLoading function to update whether it is loading
 * and can use the `isLoading` value to make logical decisions.
 *
 * # Examples:
 *
 * ## Child component using LoadingContainer interfaces
 *
 * This example shows using a child component `Example`
 * which has props that extends from `ILoadingControl` and so
 * accepts the setIsLoading function from the loading container.
 *
 * ```tsx
 * <LoadingContainer>
 *      {({ setIsLoading }): JSX.Element => (
 *          <Example setIsLoading={setIsLoading}
 *              {...props}
 *          />
 *      )}
 * </LoadingContainer>
 * ```
 *
 * ## Child component not supporting LoadingContainer interfaces
 *
 * The following example is using a child component (iframe) that does
 * not extends from on of the interfaces but can still be used.
 *
 * This example will create a new event handler based on the setIsLoading
 * function this allows for other logic to occur when onLoad is fired on
 * top of updating the loading to false.
 *
 * ```tsx
 * const createOnLoadHandle = (setIsLoading: (isLoading: boolean) => void) => (
 *  (event: React.SyntheticEvent<HTMLIFrameElement>): void => {
 *      // Pass event through if neccessary.
 *      if (props.onLoad) {
 *          props.onLoad(event);
 *      }
 *      setIsLoading(false);
 *  }
 * );
 *
 * return (
 *  <LoadingContainer loadingProps={props.loadingProps}>
 *      {({ setIsLoading }): JSX.Element => (
 *          <iframe onLoad={createOnLoadHandle(setIsLoading)}
 *              {...props}
 *          />
 *      )}
 *  </LoadingContainer>
 * );
 * ```
 */
export const LoadingContainer = ({
    children,
    loadingProps,
}: ILoadingContainerProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(true);

    return (
        <React.Fragment>
            {isLoading && <Loading {...loadingProps} />}
            {children({
                isLoading,
                setIsLoading,
            })}
        </React.Fragment>
    );
};
